import type {
  CanonicalTreeBranchConsumerResult,
} from '../tree/CanonicalTreeBranchConsumerAuthority';

import type {
  CanonicalTreeLayoutPathRuntimeSelectorResult,
} from '../tree/CanonicalTreeLayoutPathRuntimeSelector';

export interface TreeRuntimeBranchLayoutAuthoritySnapshotInput {
  readonly viewerMemberId:
    string | null | undefined;

  readonly treeVisibleMemberIds?:
    ReadonlySet<string>;

  /*
   * CC-TREE-LIVE-003G3G complete-evidence authority.
   *
   * These values are intentionally optional for historical callers.
   * Missing evidence FAILS CLOSED to the caller's existing selector
   * result rather than granting canonical promotion.
   */
  readonly presentationHasProjection?:
    boolean;

  readonly presentationUnresolvedEvidenceCount?:
    number;

  readonly presentationConflictingEvidenceCount?:
    number;

  readonly presentationInvalidCompatibilityEvidenceCount?:
    number;

  readonly geometryRoleMemberIds?:
    ReadonlySet<string>;

  readonly geometryMissingProjectionMemberIdsCount?:
    number;

  readonly branch:
    CanonicalTreeBranchConsumerResult;

  readonly layoutPath:
    CanonicalTreeLayoutPathRuntimeSelectorResult;
}

export interface TreeRuntimeBranchLayoutAuthoritySnapshot {
  readonly authority:
    'CC-TREE-LIVE-003G2K2';

  readonly viewerMemberId:
    string | null;

  readonly branch:
    CanonicalTreeBranchConsumerResult;

  readonly layoutPath:
    CanonicalTreeLayoutPathRuntimeSelectorResult;
}

class TreeRuntimeReadonlyStringMap
  implements ReadonlyMap<string, string>
{
  #values: Map<string, string>;

  constructor(
    values: ReadonlyMap<string, string>,
  ) {
    this.#values =
      new Map(values);

    Object.freeze(this);
  }

  get size(): number {
    return this.#values.size;
  }

  get(
    key: string,
  ): string | undefined {
    return this.#values.get(key);
  }

  has(
    key: string,
  ): boolean {
    return this.#values.has(key);
  }

  forEach(
    callback: (
      value: string,
      key: string,
      map: ReadonlyMap<string, string>,
    ) => void,
    thisArg?: any,
  ): void {
    for (
      const [key, value]
      of this.#values
    ) {
      callback.call(
        thisArg,
        value,
        key,
        this,
      );
    }
  }

  entries() {
    return this.#values.entries();
  }

  keys() {
    return this.#values.keys();
  }

  values() {
    return this.#values.values();
  }

  [Symbol.iterator]() {
    return this.#values[Symbol.iterator]();
  }
}

const createReadonlyMap = (
  values: ReadonlyMap<string, string>,
): ReadonlyMap<string, string> =>
  new TreeRuntimeReadonlyStringMap(
    values,
  );

const mapCoversVisibleMembers = (
  values: ReadonlyMap<string, string>,
  visibleMemberIds: ReadonlySet<string>,
): boolean =>
  visibleMemberIds.size > 0 &&
  Array.from(visibleMemberIds).every(
    memberId => values.has(memberId),
  );

const setCoversVisibleMembers = (
  values: ReadonlySet<string>,
  visibleMemberIds: ReadonlySet<string>,
): boolean =>
  visibleMemberIds.size > 0 &&
  Array.from(visibleMemberIds).every(
    memberId => values.has(memberId),
  );

const shouldPromoteCanonicalAuthority = (
  input: TreeRuntimeBranchLayoutAuthoritySnapshotInput,
): boolean => {
  const visibleMemberIds =
    input.treeVisibleMemberIds;

  const normalizedViewerMemberId =
    typeof input.viewerMemberId === 'string'
      ? input.viewerMemberId.trim()
      : '';

  const geometryRoleMemberIds =
    input.geometryRoleMemberIds;

  /*
   * Canonical promotion is fail-closed.
   *
   * Convergence diagnostics still independently report these failures,
   * but branch/layout selection itself may not become canonical before
   * the complete viewer/presentation/geometry evidence exists.
   */
  if (
    !visibleMemberIds ||
    visibleMemberIds.size === 0 ||
    normalizedViewerMemberId.length === 0 ||
    !visibleMemberIds.has(
      normalizedViewerMemberId,
    ) ||
    input.presentationHasProjection !== true ||
    input.presentationUnresolvedEvidenceCount !== 0 ||
    input.presentationConflictingEvidenceCount !== 0 ||
    input.presentationInvalidCompatibilityEvidenceCount !== 0 ||
    !geometryRoleMemberIds ||
    input.geometryMissingProjectionMemberIdsCount !== 0
  ) {
    return false;
  }

  return (
    mapCoversVisibleMembers(
      input.branch.canonicalBranchPathByMemberId,
      visibleMemberIds,
    ) &&
    mapCoversVisibleMembers(
      input.layoutPath.canonicalCompatiblePathByMemberId,
      visibleMemberIds,
    ) &&
    setCoversVisibleMembers(
      geometryRoleMemberIds,
      visibleMemberIds,
    ) &&
    input.branch.unresolvedEvidenceCount === 0 &&
    input.branch.conflictingEvidenceCount === 0 &&
    input.layoutPath.unresolvedEvidenceCount === 0 &&
    input.layoutPath.conflictingEvidenceCount === 0 &&
    input.layoutPath.invalidCompatibilityEvidenceCount === 0
  );
};

const freezeBranch = (
  branch: CanonicalTreeBranchConsumerResult,
): CanonicalTreeBranchConsumerResult =>
  Object.freeze({
    ...branch,
    branchPathByMemberId:
      createReadonlyMap(branch.branchPathByMemberId),
    legacyBranchPathByMemberId:
      createReadonlyMap(branch.legacyBranchPathByMemberId),
    canonicalBranchPathByMemberId:
      createReadonlyMap(branch.canonicalBranchPathByMemberId),
  });

const freezeLayoutPath = (
  layoutPath:
    CanonicalTreeLayoutPathRuntimeSelectorResult,
): CanonicalTreeLayoutPathRuntimeSelectorResult =>
  Object.freeze({
    ...layoutPath,
    branchPathByMemberId:
      createReadonlyMap(layoutPath.branchPathByMemberId),
    legacyPathByMemberId:
      createReadonlyMap(layoutPath.legacyPathByMemberId),
    canonicalCompatiblePathByMemberId:
      createReadonlyMap(
        layoutPath
          .canonicalCompatiblePathByMemberId,
      ),
  });

export const createTreeRuntimeBranchLayoutAuthoritySnapshot = (
  input:
    TreeRuntimeBranchLayoutAuthoritySnapshotInput,
): TreeRuntimeBranchLayoutAuthoritySnapshot => {
  const normalizedViewerMemberId =
    typeof input.viewerMemberId === 'string'
      ? input.viewerMemberId.trim()
      : '';

  const viewerMemberId =
    normalizedViewerMemberId.length > 0
      ? normalizedViewerMemberId
      : null;

  const promoteCanonicalAuthority =
    shouldPromoteCanonicalAuthority(input);

  const activeBranch:
    CanonicalTreeBranchConsumerResult =
      promoteCanonicalAuthority
        ? {
            ...input.branch,
            source: 'canonical',
            migrationReadiness:
              input.branch
                .canonicalAdditionCount > 0
                ? 'READY-WITH-CANONICAL-ADDITIONS'
                : 'READY',
            branchPathByMemberId:
              input.branch.canonicalBranchPathByMemberId,
          }
        : input.branch;

  const activeLayoutPath:
    CanonicalTreeLayoutPathRuntimeSelectorResult =
      promoteCanonicalAuthority
        ? {
            ...input.layoutPath,
            source: 'canonical-compatible',
            readiness:
              input.layoutPath
                .canonicalAdditionCount > 0
                ? 'READY-WITH-CANONICAL-ADDITIONS'
                : 'READY',
            branchPathByMemberId:
              input.layoutPath
                .canonicalCompatiblePathByMemberId,
          }
        : input.layoutPath;

  return Object.freeze({
    authority:
      'CC-TREE-LIVE-003G2K2',
    viewerMemberId,
    branch:
      freezeBranch(activeBranch),
    layoutPath:
      freezeLayoutPath(activeLayoutPath),
  });
};
